import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Alert } from '@/components';
import { useForgotPasswordMutation } from '@/services/AuthService';
import { useSnackbar } from 'notistack';
import { toAbsoluteUrl } from '@/utils';
import { getErrorMessage } from '@/utils/errorUtils';

const maskEmail = (email: string) => {
  const [localPart = '', domain = ''] = email.split('@');

  if (!localPart || !domain) {
    return email;
  }

  if (localPart.length <= 2) {
    return `${localPart[0] || ''}***@${domain}`;
  }

  return `${localPart.slice(0, 2)}***${localPart.slice(-1)}@${domain}`;
};

const ResetPasswordCheckEmail = () => {
  const { state } = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const [forgotPassword] = useForgotPasswordMutation();
  const [isResending, setIsResending] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const email =
    (state as { email?: string } | null)?.email ||
    localStorage.getItem('resetEmail') ||
    '';

  const handleResend = async () => {
    if (!email || isResending) {
      return;
    }

    try {
      setIsResending(true);
      setErrorMessage('');

      const response = await forgotPassword({
        email,
        isAdminPanel: true,
      }).unwrap();

      enqueueSnackbar(
        response.message || 'Reset link sent again successfully.',
        {
          variant: 'success',
        }
      );
    } catch (error) {
      const message = getErrorMessage(
        error,
        'Unable to resend the password reset link. Please try again.'
      );

      setErrorMessage(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="card max-w-[440px] w-full contrast:!bg-card">
      <div className="card-body p-10">
        <div className="flex justify-center py-10">
          <img
            src={toAbsoluteUrl('/media/illustrations/30.svg')}
            className="dark:hidden contrast:hidden max-h-[130px]"
            alt="Check your email"
          />
          <img
            src={toAbsoluteUrl('/media/illustrations/30-dark.svg')}
            className="hidden dark:block contrast:block max-h-[130px]"
            alt="Check your email"
          />
        </div>

        <h1 className="text-lg font-medium text-gray-900 text-center mb-3">
          Check your email
        </h1>
        <div className="text-2sm text-center text-gray-700 mb-7.5">
          We&apos;ve sent a password reset link to{' '}
          <span className="text-gray-900 font-medium break-all">
            {email ? maskEmail(email) : 'your email address'}
          </span>
          . Open that email and follow the link to set a new password.
        </div>

        {errorMessage && (
          <Alert variant="danger" className="mb-5">
            {errorMessage}
          </Alert>
        )}

        <div className="flex justify-center mb-5">
          <Link
            to="/auth/login"
            className="btn btn-primary flex justify-center"
          >
            Back to Login
          </Link>
        </div>

        <div className="flex items-center justify-center gap-1 text-center">
          <span className="text-xs text-gray-600">
            Didn&apos;t receive an email?
          </span>
          <button
            type="button"
            onClick={handleResend}
            disabled={!email || isResending}
            className="text-xs contrast:text-blue-400 font-medium link disabled:text-gray-400 disabled:no-underline"
          >
            {isResending ? 'Sending...' : 'Resend'}
          </button>
        </div>
      </div>
    </div>
  );
};

export { ResetPasswordCheckEmail };
